/*
 * Copyright (c) 2026 Macrofocus GmbH. All Rights Reserved.
 */
package com.macrofocus.docking.splitter

import kotlin.math.max

/**
 * Position and extent, along the split axis, of the five slots of a three components splitter.
 * A divider whose size is 0 is not visible.
 */
data class ThreeComponentsBounds(
    val firstStart: Int,
    val firstSize: Int,
    val firstDividerStart: Int,
    val firstDividerSize: Int,
    val innerStart: Int,
    val innerSize: Int,
    val lastDividerStart: Int,
    val lastDividerSize: Int,
    val lastStart: Int,
    val lastSize: Int,
) {
    val firstDividerVisible: Boolean get() = firstDividerSize > 0
    val lastDividerVisible: Boolean get() = lastDividerSize > 0
}

/**
 * Toolkit independent layout algorithm of the three components splitter. It mirrors the behavior of the reference
 * Swing implementation (`SwingThreeComponentsSplitter.doLayout`) so that the Compose and React implementations
 * lay out their components identically:
 *
 *  - the first and last components get their preferred size, the inner component gets the remaining space;
 *  - a divider is only shown between two visible components;
 *  - when space is lacking, the first component shrinks;
 *  - when the inner component is hidden, the last (or else the first) component absorbs its space.
 */
object ThreeComponentsLayout {
    const val DEFAULT_DIVIDER_WIDTH = 8

    fun firstDividerVisible(firstVisible: Boolean, innerVisible: Boolean, lastVisible: Boolean): Boolean =
        firstVisible && innerVisible || firstVisible && lastVisible && !innerVisible

    fun lastDividerVisible(innerVisible: Boolean, lastVisible: Boolean): Boolean = innerVisible && lastVisible

    fun compute(
        total: Int,
        firstVisible: Boolean,
        innerVisible: Boolean,
        lastVisible: Boolean,
        firstSize: Double,
        lastSize: Double,
        dividerWidth: Int = DEFAULT_DIVIDER_WIDTH,
    ): ThreeComponentsBounds {
        val firstDividerVisible = firstDividerVisible(firstVisible, innerVisible, lastVisible)
        val lastDividerVisible = lastDividerVisible(innerVisible, lastVisible)
        val dividersCount = (if (firstDividerVisible) 1 else 0) + (if (lastDividerVisible) 1 else 0)

        var divider = dividerWidth
        var first: Double
        var last: Double
        var inner: Double
        if (total <= dividersCount * dividerWidth) {
            first = 0.0
            last = 0.0
            inner = 0.0
            divider = if (dividersCount > 0) max(0, total) / dividersCount else 0
        } else {
            first = if (firstVisible) firstSize else 0.0
            last = if (lastVisible) lastSize else 0.0
            val available = total - dividersCount * dividerWidth
            val sizeLack = (first + last).toInt() - available
            if (sizeLack > 0) {
                first -= sizeLack.toDouble()
                if (first < 0.0) {
                    // Swing would let the first component get a negative size; give the remainder to the last one instead
                    last += first
                    first = 0.0
                }
                inner = 0.0
            } else {
                inner = available - first - last
            }
            if (!innerVisible) {
                if (lastVisible) {
                    last += inner
                } else {
                    first += inner
                }
                inner = 0.0
            }
        }

        var space = first.toInt()
        val firstDividerStart = space
        if (firstDividerVisible) space += divider
        val innerStart = space
        if (innerVisible) space += inner.toInt()
        val lastDividerStart = space
        if (lastDividerVisible) space += divider
        val lastStart = space

        return ThreeComponentsBounds(
            firstStart = 0,
            firstSize = first.toInt(),
            firstDividerStart = firstDividerStart,
            firstDividerSize = if (firstDividerVisible) divider else 0,
            innerStart = innerStart,
            innerSize = if (innerVisible) inner.toInt() else 0,
            lastDividerStart = lastDividerStart,
            lastDividerSize = if (lastDividerVisible) divider else 0,
            lastStart = lastStart,
            lastSize = last.toInt(),
        )
    }

    /** New first size when the first divider is dragged to [position] (in splitter coordinates), as in Swing. */
    fun firstSizeForDividerAt(position: Double): Double = max(0.0, position)

    /** New last size when the last divider is dragged to [position] (in splitter coordinates), as in Swing. */
    fun lastSizeForDividerAt(position: Double, total: Double, dividerWidth: Int = DEFAULT_DIVIDER_WIDTH): Double =
        max(0.0, total - position - dividerWidth)
}
